import { useCallback, useEffect, useState } from "react";
import { api, apiError, fmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImageField } from "@/components/ImageField";
import { QuestStepBuilder } from "@/components/QuestStepBuilder";
import { QuestReview, QuestRevenue } from "@/components/QuestReview";
import { QuestIntegration } from "@/components/QuestIntegration";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, RefreshCw, UserCheck, Link2 } from "lucide-react";

const SELECT = "mt-2 w-full rounded-none border border-input bg-secondary px-3 py-2 text-sm";

const QUEST_BLANK = {
  campaign_id: "", title: "", description: "", image_url: "", offer_text: "",
  action_type: "signup", tracking: "proof", cadence: "once", points: 1000,
  category: "other",
  target_url: "", cta_label: "Start quest", proof_instructions: "", proof_media: "image",
  logo_url: "",
  watch_minutes: 0,
  min_level: 1, max_completions: 0, publish_at: "", expires_at: "", active: true,
  steps: [], step_mode: "sequential",
};
const BRAND_BLANK = {
  name: "", logo_url: "", website: "", contact_name: "", contact_email: "", notes: "", active: true,
};
const CAMPAIGN_BLANK = {
  brand_id: "", name: "", cpa_amount: 100, currency: "USD", budget_amount: 0,
  max_conversions: 0, starts_at: "", ends_at: "", auth_mode: "key", ip_allowlist: "",
  notes: "", active: true,
};

const ACTIONS = [
  ["signup", "Sign up / registration"], ["purchase", "Purchase"], ["deposit", "Deposit"],
  ["link", "Link an account"], ["ingame", "In-game challenge"], ["visit", "Visit / watch"],
  ["custom", "Custom action"],
];
const TRACKING = [
  ["postback", "Server-to-server postback (auto award)"],
  ["pixel", "Tracking pixel (auto award)"],
  ["proof", "Player uploads proof (admin review)"],
  ["manual", "Manual — admin marks complete"],
  ["watch", "Stream watch time (auto award)"],
];
const CADENCES = [["once", "One time"], ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]];
const QUEST_CATEGORIES = [
  ["retail", "Retail"], ["dining", "Dining"], ["social", "Social"], ["video", "Video"],
  ["gaming", "Gaming"], ["ingame", "In-game"], ["betting", "Betting"], ["other", "Other"],
];

const copy = (text, label) => {
  const p = navigator.clipboard?.writeText(text);
  if (!p) {
    toast.error("Clipboard blocked — select and copy manually");
    return;
  }
  p.then(() => toast.success(`${label} copied`)).catch(() =>
    toast.error("Clipboard blocked — select and copy manually")
  );
};

const Field = ({ label, children, hint }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
  </div>
);

function QuestForm({ campaigns, value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Campaign" hint="Leave blank for a LiquidRP house quest (no brand, no CPA)">
        <select className={SELECT} data-testid="quest-campaign" value={value.campaign_id}
          onChange={(e) => {
            const id = e.target.value;
            const patch = { campaign_id: id };
            if (!id && (value.tracking === "postback" || value.tracking === "pixel")) {
              patch.tracking = "proof";
            }
            onChange({ ...value, ...patch });
          }}>
          <option value="">LiquidRP house quest (no brand)</option>
          {campaigns.map((c) => {
            const label = `${c.brand_name} — ${c.name} ($${c.cpa_amount} CPA)`;
            return <option key={c.id} value={c.id}>{label}</option>;
          })}
        </select>
      </Field>
      <Field label="Title">
        <Input className="mt-2 rounded-none bg-secondary" data-testid="quest-title"
          value={value.title} onChange={(e) => set("title", e.target.value)} />
      </Field>
      <div className="md:col-span-2">
        <Field label="Description (player facing)">
          <Textarea className="mt-2 rounded-none bg-secondary" data-testid="quest-description"
            value={value.description} onChange={(e) => set("description", e.target.value)} />
        </Field>
      </div>
      <div className="md:col-span-2">
        <Field label="Brand offer shown to the player"
          hint="e.g. Sign up and get $5 free, plus 150 free play on every $10 deposit">
          <Textarea className="mt-2 rounded-none bg-secondary" data-testid="quest-offer"
            value={value.offer_text} onChange={(e) => set("offer_text", e.target.value)} />
        </Field>
      </div>
      <Field label="Action type">
        <select className={SELECT} data-testid="quest-action" value={value.action_type}
          onChange={(e) => set("action_type", e.target.value)}>
          {ACTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Field>
      <Field label="How we track it">
        <select className={SELECT} data-testid="quest-tracking" value={value.tracking}
          onChange={(e) => set("tracking", e.target.value)}>
          {TRACKING.filter(([v]) => value.campaign_id || !["postback", "pixel"].includes(v))
            .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Field>
      <Field label="Publishing cadence" hint="Repeatable quests reset every period">
        <select className={SELECT} data-testid="quest-cadence" value={value.cadence}
          onChange={(e) => set("cadence", e.target.value)}>
          {CADENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Field>
      <Field label="Category" hint="Drives the filter pills on the player quest page">
        <select className={SELECT} data-testid="quest-category" value={value.category || "other"}
          onChange={(e) => set("category", e.target.value)}>
          {QUEST_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Field>
      {value.tracking === "watch" && (
        <Field label="Watch minutes required"
          hint="Minutes of Watch & earn stream time. Counted over the cadence above — daily resets each day, weekly each Monday, one time counts all-time.">
          <Input type="number" min="1" className="mt-2 rounded-none bg-secondary mono"
            data-testid="quest-watch-minutes" value={value.watch_minutes || 0}
            onChange={(e) => set("watch_minutes", Number(e.target.value))} />
        </Field>
      )}
      <Field label="Points awarded">
        <Input type="number" className="mt-2 rounded-none bg-secondary mono" data-testid="quest-points"
          value={value.points} onChange={(e) => set("points", Number(e.target.value))} />
      </Field>
      <div className="md:col-span-2">
        <Field label="Target URL"
          hint="Use {click_id} and {sig} anywhere in the link; if you don't, we append them as query params">
          <Input className="mt-2 rounded-none bg-secondary mono" data-testid="quest-url"
            placeholder="https://prizepicks.com/signup?ref=liquidrp&sub1={click_id}"
            value={value.target_url} onChange={(e) => set("target_url", e.target.value)} />
        </Field>
      </div>
      <Field label="Button label">
        <Input className="mt-2 rounded-none bg-secondary" data-testid="quest-cta"
          value={value.cta_label} onChange={(e) => set("cta_label", e.target.value)} />
      </Field>
      <Field label="Proof media accepted">
        <select className={SELECT} data-testid="quest-proof-media" value={value.proof_media}
          onChange={(e) => set("proof_media", e.target.value)}>
          <option value="image">Image only</option>
          <option value="video">Video only</option>
          <option value="any">Image or video</option>
        </select>
      </Field>
      <div className="md:col-span-2">
        <QuestStepBuilder
          steps={value.steps || []}
          stepMode={value.step_mode || "sequential"}
          onChange={(steps) => set("steps", steps)}
          onModeChange={(m) => set("step_mode", m)}
        />
      </div>
      <div className="md:col-span-2">
        <Field label="Single-proof instructions (used only when there are no steps)"
          hint="Shown in the upload dialog — tell them exactly what the screenshot or clip must show">
          <Textarea className="mt-2 rounded-none bg-secondary" data-testid="quest-proof-instructions"
            value={value.proof_instructions}
            onChange={(e) => set("proof_instructions", e.target.value)} />
        </Field>
      </div>
      <Field label="Minimum level">
        <Input type="number" className="mt-2 rounded-none bg-secondary mono" data-testid="quest-min-level"
          value={value.min_level} onChange={(e) => set("min_level", Number(e.target.value))} />
      </Field>
      <Field label="Max completions (0 = unlimited)">
        <Input type="number" className="mt-2 rounded-none bg-secondary mono" data-testid="quest-max"
          value={value.max_completions}
          onChange={(e) => set("max_completions", Number(e.target.value))} />
      </Field>
      <Field label="Publish at (blank = now)">
        <Input type="datetime-local" className="mt-2 rounded-none bg-secondary" data-testid="quest-publish"
          value={(value.publish_at || "").slice(0, 16)}
          onChange={(e) => set("publish_at", e.target.value)} />
      </Field>
      <Field label="Expires at (blank = never)">
        <Input type="datetime-local" className="mt-2 rounded-none bg-secondary" data-testid="quest-expires"
          value={(value.expires_at || "").slice(0, 16)}
          onChange={(e) => set("expires_at", e.target.value)} />
      </Field>
      <div className="md:col-span-2">
        <Field label="Circle logo (optional)"
          hint="Shown as the round badge on the quest tile. Leave blank to use the brand's logo. Square images crop best.">
          <ImageField value={value.logo_url} onChange={(v) => set("logo_url", v)} testid="quest-logo" />
        </Field>
      </div>
      <div className="md:col-span-2">
        <Field label="Card image (optional)"
          hint="Wide artwork shown when a player opens the quest. Doesn't change the tile size.">
          <ImageField value={value.image_url} onChange={(v) => set("image_url", v)} testid="quest-image" />
        </Field>
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={!!value.active} onCheckedChange={(v) => set("active", v)} data-testid="quest-active" />
        <span className="text-sm">Live for players</span>
      </div>
    </div>
  );
}

function ManualComplete({ quest, onDone }) {
  const [q, setQ] = useState("");
  const [players, setPlayers] = useState([]);
  const [note, setNote] = useState("");

  const search = async () => {
    const { data } = await api.get("/admin/players", { params: { q } });
    setPlayers(data.slice(0, 8));
  };

  const complete = async (uid) => {
    try {
      const { data } = await api.post("/admin/quest-attempts/manual", {
        user_id: uid, quest_id: quest.id, note,
      });
      toast.success(`Marked complete — ${fmt(data.points)} points awarded`);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="manual-complete">
      <div className="flex gap-2">
        <Input className="rounded-none bg-secondary" placeholder="Search player name or email"
          value={q} onChange={(e) => setQ(e.target.value)} data-testid="manual-search-input" />
        <Button variant="outline" className="rounded-none" onClick={search} data-testid="manual-search-btn">
          Search
        </Button>
      </div>
      <Textarea className="mt-3 rounded-none bg-secondary" placeholder="Note (optional)"
        value={note} onChange={(e) => setNote(e.target.value)} data-testid="manual-note" />
      <div className="mt-3 border border-border">
        {players.length === 0 && (
          <p className="p-4 text-xs text-muted-foreground">Search for the player to credit.</p>
        )}
        {players.map((p) => (
          <div key={p.id} className="flex items-center justify-between border-b border-border/60 px-4 py-3 last:border-0">
            <div>
              <div className="text-sm">{p.username}</div>
              <div className="text-xs text-muted-foreground">{p.email}</div>
            </div>
            <Button size="sm" className="rounded-none" onClick={() => complete(p.id)}
              data-testid={`manual-award-${p.id}`}>
              <UserCheck className="mr-2 h-3.5 w-3.5" /> Mark complete
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestList({ campaigns, reload }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(QUEST_BLANK);
  const [manualFor, setManualFor] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/quests");
    setRows(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!form.campaign_id && (form.tracking === "postback" || form.tracking === "pixel")) {
      toast.error("House quests can only use proof upload or manual review");
      return;
    }
    if (!form.title) {
      toast.error("Give the quest a title");
      return;
    }
    try {
      if (editing === "new") await api.post("/admin/quests", form);
      else await api.put(`/admin/quests/${editing}`, form);
      toast.success("Quest saved");
      setEditing(null);
      load();
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/quests/${id}`);
      toast.success("Quest deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="quest-list">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold md:text-lg">Quests</h3>
        <Button className="rounded-none" data-testid="quest-new-btn"
          onClick={() => { setForm(QUEST_BLANK); setEditing("new"); }}>
          <Plus className="mr-2 h-4 w-4" /> New quest
        </Button>
      </div>

      {campaigns.length === 0 && (
        <p className="mt-4 border border-border bg-card p-6 text-sm text-muted-foreground">
          No brand campaigns yet — you can still build LiquidRP house quests (leave the campaign
          blank). Add a brand + campaign when you want a sponsored quest with a CPA attached.
        </p>
      )}

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        {rows.map((r) => (
          <div key={r.id} className="border border-border bg-card p-5" data-testid={`quest-row-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                {(r.logo_url || r.image_url) && (
                  <img src={r.logo_url || r.image_url} alt=""
                    className="h-9 w-9 shrink-0 rounded-full border border-primary/30 object-cover" />
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    {r.brand_name} · {r.campaign_name}
                  </div>
                  <h4 className="mt-1 font-semibold">{r.title}</h4>
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" data-testid={`quest-edit-${r.id}`}
                  onClick={() => { setForm({ ...QUEST_BLANK, ...r }); setEditing(r.id); }}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" data-testid={`quest-delete-${r.id}`}
                  onClick={() => remove(r.id)}>
                  <Trash2 className="h-4 w-4 text-red-400" />
                </Button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge className="rounded-none uppercase tracking-wider">
                {r.sponsored ? "Sponsored" : "House"}
              </Badge>
              <Badge className="rounded-none uppercase tracking-wider">{r.cadence}</Badge>
              <Badge variant="outline" className="rounded-none uppercase tracking-wider">{r.action_type}</Badge>
              <Badge variant="outline" className="rounded-none uppercase tracking-wider">{r.tracking}</Badge>
              <Badge variant="outline" className="rounded-none uppercase tracking-wider">{r.category || "other"}</Badge>
              {!r.active && <Badge variant="destructive" className="rounded-none">paused</Badge>}
            </div>
            <div className="mono mt-4 grid grid-cols-4 gap-2 text-center text-xs">
              <div><div className="text-muted-foreground">Starts</div>{fmt(r.stats.clicks)}</div>
              <div><div className="text-muted-foreground">Pending</div>{fmt(r.stats.pending)}</div>
              <div><div className="text-muted-foreground">Done</div>{fmt(r.stats.approved)}</div>
              <div><div className="text-muted-foreground">Revenue</div>${fmt(Math.round(r.stats.revenue))}</div>
            </div>
            <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
              <span className="mono text-primary">+{fmt(r.total_points ?? r.points)} pts to player</span>
              <span>
                {r.step_count > 0 ? `${r.step_count} steps · ` : ""}
                {r.sponsored ? `$${r.cpa_amount} CPA in` : "No CPA (house)"}
              </span>
            </div>
            <Button variant="outline" size="sm" className="mt-4 w-full rounded-none"
              onClick={() => setManualFor(r)} data-testid={`quest-manual-${r.id}`}>
              <UserCheck className="mr-2 h-3.5 w-3.5" /> Credit a player manually
            </Button>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-border bg-card sm:max-w-3xl"
          data-testid="quest-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New quest" : "Edit quest"}</DialogTitle>
          </DialogHeader>
          <QuestForm campaigns={campaigns} value={form} onChange={setForm} />
          <Button className="rounded-none" onClick={save} data-testid="quest-save">Save quest</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!manualFor} onOpenChange={(o) => !o && setManualFor(null)}>
        <DialogContent className="rounded-none border-border bg-card sm:max-w-lg" data-testid="manual-dialog">
          <DialogHeader>
            <DialogTitle>Credit a player — {manualFor?.title}</DialogTitle>
          </DialogHeader>
          {manualFor && <ManualComplete quest={manualFor} onDone={() => { setManualFor(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BrandsAndCampaigns({ brands, campaigns, reload }) {
  const [brandForm, setBrandForm] = useState(null);
  const [campForm, setCampForm] = useState(null);
  const [tracking, setTracking] = useState(null);

  const saveBrand = async () => {
    try {
      if (brandForm.id) await api.put(`/admin/brands/${brandForm.id}`, brandForm);
      else await api.post("/admin/brands", brandForm);
      toast.success("Brand saved");
      setBrandForm(null);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveCampaign = async () => {
    try {
      if (campForm.id) await api.put(`/admin/campaigns/${campForm.id}`, campForm);
      else await api.post("/admin/campaigns", campForm);
      toast.success("Campaign saved");
      setCampForm(null);
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const del = async (kind, id) => {
    try {
      await api.delete(`/admin/${kind}/${id}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const rotate = async (id) => {
    try {
      const { data } = await api.post(`/admin/campaigns/${id}/rotate-keys`);
      setTracking(data);
      toast.success("New keys generated — send them to the brand");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="brands-campaigns">
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold md:text-lg">Brands</h3>
          <Button className="rounded-none" data-testid="brand-new-btn"
            onClick={() => setBrandForm({ ...BRAND_BLANK })}>
            <Plus className="mr-2 h-4 w-4" /> New brand
          </Button>
        </div>
        <div className="mt-5 border border-border bg-card">
          {brands.length === 0 && <p className="p-6 text-sm text-muted-foreground">No brands yet.</p>}
          {brands.map((b) => (
            <div key={b.id} className="flex items-center justify-between border-b border-border/60 px-5 py-4 last:border-0"
              data-testid={`brand-row-${b.id}`}>
              <div className="flex items-center gap-3">
                {b.logo_url && <img src={b.logo_url} alt={b.name} className="h-8 w-8 object-contain" />}
                <div>
                  <div className="text-sm font-medium">{b.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {b.campaign_count} campaign(s){b.website ? ` · ${b.website}` : ""}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!b.active && <Badge variant="destructive" className="rounded-none">paused</Badge>}
                <Button size="icon" variant="ghost" data-testid={`brand-edit-${b.id}`}
                  onClick={() => setBrandForm(b)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" data-testid={`brand-delete-${b.id}`}
                  onClick={() => del("brands", b.id)}>
                  <Trash2 className="h-4 w-4 text-red-400" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold md:text-lg">Campaigns</h3>
          <Button className="rounded-none" data-testid="campaign-new-btn"
            onClick={() => setCampForm({ ...CAMPAIGN_BLANK, brand_id: brands[0]?.id || "" })}>
            <Plus className="mr-2 h-4 w-4" /> New campaign
          </Button>
        </div>
        <div className="mt-5 border border-border bg-card">
          {campaigns.length === 0 && <p className="p-6 text-sm text-muted-foreground">No campaigns yet.</p>}
          {campaigns.map((c) => (
            <div key={c.id} className="border-b border-border/60 px-5 py-4 last:border-0"
              data-testid={`campaign-row-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">{c.brand_name} — {c.name}</div>
                  <div className="mono text-xs text-muted-foreground">
                    ${c.cpa_amount} {c.currency} CPA · {fmt(c.conversions)} conv · ${fmt(Math.round(c.revenue))} revenue
                    {c.budget_amount ? ` · budget $${fmt(c.budget_amount)}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!c.active && <Badge variant="destructive" className="rounded-none">paused</Badge>}
                  <Button size="icon" variant="ghost" data-testid={`campaign-tracking-${c.id}`}
                    onClick={() => setTracking(c)}>
                    <Link2 className="h-4 w-4 text-primary" />
                  </Button>
                  <Button size="icon" variant="ghost" data-testid={`campaign-edit-${c.id}`}
                    onClick={() => setCampForm(c)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" data-testid={`campaign-delete-${c.id}`}
                    onClick={() => del("campaigns", c.id)}>
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!brandForm} onOpenChange={(o) => !o && setBrandForm(null)}>
        <DialogContent className="rounded-none border-border bg-card sm:max-w-lg" data-testid="brand-dialog">
          <DialogHeader><DialogTitle>{brandForm?.id ? "Edit brand" : "New brand"}</DialogTitle></DialogHeader>
          {brandForm && (
            <div className="grid gap-4">
              <Field label="Brand name">
                <Input className="mt-2 rounded-none bg-secondary" data-testid="brand-name"
                  value={brandForm.name} onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })} />
              </Field>
              <Field label="Website">
                <Input className="mt-2 rounded-none bg-secondary" data-testid="brand-website"
                  value={brandForm.website} onChange={(e) => setBrandForm({ ...brandForm, website: e.target.value })} />
              </Field>
              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Contact name">
                  <Input className="mt-2 rounded-none bg-secondary" data-testid="brand-contact"
                    value={brandForm.contact_name}
                    onChange={(e) => setBrandForm({ ...brandForm, contact_name: e.target.value })} />
                </Field>
                <Field label="Contact email">
                  <Input className="mt-2 rounded-none bg-secondary" data-testid="brand-email"
                    value={brandForm.contact_email}
                    onChange={(e) => setBrandForm({ ...brandForm, contact_email: e.target.value })} />
                </Field>
              </div>
              <Field label="Internal notes">
                <Textarea className="mt-2 rounded-none bg-secondary" data-testid="brand-notes"
                  value={brandForm.notes} onChange={(e) => setBrandForm({ ...brandForm, notes: e.target.value })} />
              </Field>
              <Field label="Logo">
                <ImageField value={brandForm.logo_url}
                  onChange={(v) => setBrandForm({ ...brandForm, logo_url: v })} testid="brand-logo" />
              </Field>
              <div className="flex items-center gap-3">
                <Switch checked={!!brandForm.active} data-testid="brand-active"
                  onCheckedChange={(v) => setBrandForm({ ...brandForm, active: v })} />
                <span className="text-sm">Active</span>
              </div>
              <Button className="rounded-none" onClick={saveBrand} data-testid="brand-save">Save brand</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!campForm} onOpenChange={(o) => !o && setCampForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-border bg-card sm:max-w-2xl"
          data-testid="campaign-dialog">
          <DialogHeader><DialogTitle>{campForm?.id ? "Edit campaign" : "New campaign"}</DialogTitle></DialogHeader>
          {campForm && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Brand">
                <select className={SELECT} data-testid="campaign-brand" value={campForm.brand_id}
                  onChange={(e) => setCampForm({ ...campForm, brand_id: e.target.value })}>
                  <option value="">Pick a brand…</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Campaign name">
                <Input className="mt-2 rounded-none bg-secondary" data-testid="campaign-name"
                  value={campForm.name} onChange={(e) => setCampForm({ ...campForm, name: e.target.value })} />
              </Field>
              <Field label="CPA the brand pays us" hint="Per approved conversion">
                <Input type="number" step="0.01" className="mt-2 rounded-none bg-secondary mono"
                  data-testid="campaign-cpa" value={campForm.cpa_amount}
                  onChange={(e) => setCampForm({ ...campForm, cpa_amount: Number(e.target.value) })} />
              </Field>
              <Field label="Currency">
                <Input className="mt-2 rounded-none bg-secondary" data-testid="campaign-currency"
                  value={campForm.currency}
                  onChange={(e) => setCampForm({ ...campForm, currency: e.target.value })} />
              </Field>
              <Field label="Budget cap (0 = uncapped)" hint="Quests hide once the budget is used up">
                <Input type="number" className="mt-2 rounded-none bg-secondary mono" data-testid="campaign-budget"
                  value={campForm.budget_amount}
                  onChange={(e) => setCampForm({ ...campForm, budget_amount: Number(e.target.value) })} />
              </Field>
              <Field label="Max conversions (0 = uncapped)">
                <Input type="number" className="mt-2 rounded-none bg-secondary mono" data-testid="campaign-max"
                  value={campForm.max_conversions}
                  onChange={(e) => setCampForm({ ...campForm, max_conversions: Number(e.target.value) })} />
              </Field>
              <Field label="Starts at">
                <Input type="datetime-local" className="mt-2 rounded-none bg-secondary" data-testid="campaign-starts"
                  value={(campForm.starts_at || "").slice(0, 16)}
                  onChange={(e) => setCampForm({ ...campForm, starts_at: e.target.value })} />
              </Field>
              <Field label="Ends at">
                <Input type="datetime-local" className="mt-2 rounded-none bg-secondary" data-testid="campaign-ends"
                  value={(campForm.ends_at || "").slice(0, 16)}
                  onChange={(e) => setCampForm({ ...campForm, ends_at: e.target.value })} />
              </Field>
              <Field label="Postback auth" hint="Key = secret in the URL. HMAC = signature required.">
                <select className={SELECT} data-testid="campaign-auth" value={campForm.auth_mode}
                  onChange={(e) => setCampForm({ ...campForm, auth_mode: e.target.value })}>
                  <option value="key">Campaign key only</option>
                  <option value="hmac">HMAC signature required</option>
                  <option value="both">Key + HMAC signature</option>
                </select>
              </Field>
              <Field label="IP allowlist" hint="Comma separated. Blank = any IP.">
                <Input className="mt-2 rounded-none bg-secondary mono" data-testid="campaign-ips"
                  value={campForm.ip_allowlist}
                  onChange={(e) => setCampForm({ ...campForm, ip_allowlist: e.target.value })} />
              </Field>
              <div className="md:col-span-2">
                <Field label="Internal notes">
                  <Textarea className="mt-2 rounded-none bg-secondary" data-testid="campaign-notes"
                    value={campForm.notes}
                    onChange={(e) => setCampForm({ ...campForm, notes: e.target.value })} />
                </Field>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={!!campForm.active} data-testid="campaign-active"
                  onCheckedChange={(v) => setCampForm({ ...campForm, active: v })} />
                <span className="text-sm">Active</span>
              </div>
              <div className="md:col-span-2">
                <Button className="w-full rounded-none" onClick={saveCampaign} data-testid="campaign-save">
                  Save campaign
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!tracking} onOpenChange={(o) => !o && setTracking(null)}>
        <DialogContent className="rounded-none border-border bg-card sm:max-w-2xl" data-testid="tracking-dialog">
          <DialogHeader><DialogTitle>Tracking setup — {tracking?.name}</DialogTitle></DialogHeader>
          {tracking && (
            <div className="space-y-4 text-sm">
              <p className="text-muted-foreground">
                Send these to the brand. We hand them a unique <span className="mono">click_id</span> (and{" "}
                <span className="mono">sig</span>) on every player click through the quest link — they pass it
                straight back so the conversion lands on the right player.
              </p>
              {[
                ["Postback URL", tracking.postback_url],
                ["Pixel URL", tracking.pixel_url],
                ["Pixel snippet", tracking.pixel_snippet],
                ["Campaign key", tracking.postback_key],
                ["HMAC secret", tracking.hmac_secret],
              ].map(([label, val]) => (
                <div key={label}>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
                  <div className="mt-1 flex gap-2">
                    <code className="mono flex-1 overflow-x-auto border border-border bg-secondary/60 px-3 py-2 text-xs">
                      {val}
                    </code>
                    <Button size="icon" variant="outline" className="rounded-none shrink-0"
                      onClick={() => copy(val, label)} data-testid={`copy-${label.replace(/\s+/g, "-").toLowerCase()}`}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" className="rounded-none" onClick={() => rotate(tracking.id)}
                data-testid="rotate-keys">
                <RefreshCw className="mr-2 h-4 w-4" /> Rotate keys
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SECTIONS = [
  ["quests", "Quests"],
  ["brands", "Brands & campaigns"],
  ["review", "Review queue"],
  ["revenue", "Revenue"],
  ["integration", "Integration & testing"],
];

export const QuestsAdmin = () => {
  const [section, setSection] = useState("quests");
  const [brands, setBrands] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  const reload = useCallback(async () => {
    const [b, c] = await Promise.all([api.get("/admin/brands"), api.get("/admin/campaigns")]);
    setBrands(b.data);
    setCampaigns(c.data);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <div data-testid="quests-admin">
      <div className="flex flex-wrap gap-2">
        {SECTIONS.map(([v, l]) => (
          <button
            key={v}
            type="button"
            onClick={() => setSection(v)}
            data-testid={`quests-section-${v}`}
            className={`border px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${
              section === v
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:text-foreground"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {section === "quests" && <QuestList campaigns={campaigns} reload={reload} />}
        {section === "brands" && (
          <BrandsAndCampaigns brands={brands} campaigns={campaigns} reload={reload} />
        )}
        {section === "review" && <QuestReview />}
        {section === "revenue" && <QuestRevenue />}
        {section === "integration" && <QuestIntegration campaigns={campaigns} />}
      </div>
    </div>
  );
};
